import { Box, type BoxProps, type StackProps, VStack } from '@chakra-ui/react'
import { Children, isValidElement, useEffect, useRef, useState } from 'react'

interface RevealProps extends BoxProps {
  /** Stagger delay in milliseconds before this element animates in. */
  delay?: number
}

// Scroll-reveal wrapper: fades and rises its children into view the first time
// they enter the viewport. Falls back to visible immediately when
// IntersectionObserver is unavailable (tests/SSR) or the user prefers reduced
// motion, so content is never hidden.
export function Reveal({ delay = 0, children, ...rest }: RevealProps) {
  const ref = useRef<HTMLDivElement>(null)
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    const el = ref.current
    if (!el) {
      return
    }
    const prefersReduced =
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    if (prefersReduced || typeof IntersectionObserver === 'undefined') {
      setVisible(true)
      return
    }
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setVisible(true)
            io.disconnect()
          }
        }
      },
      { threshold: 0.1, rootMargin: '0px 0px -40px 0px' },
    )
    io.observe(el)
    return () => io.disconnect()
  }, [])

  return (
    <Box
      ref={ref}
      opacity={visible ? 1 : 0}
      transform={visible ? 'translateY(0)' : 'translateY(18px)'}
      transition="opacity 0.55s ease, transform 0.55s ease"
      transitionDelay={`${delay}ms`}
      {...rest}
    >
      {children}
    </Box>
  )
}

// Drop-in replacement for a page's main VStack: wraps each section child in a
// Reveal so every section scroll-animates in, with a small cascade for the
// first few. Non-element children (null/false) are passed through untouched.
export function RevealStack({ children, ...props }: StackProps) {
  return (
    <VStack {...props}>
      {Children.toArray(children).map((child, i) =>
        isValidElement(child) ? (
          // biome-ignore lint/suspicious/noArrayIndexKey: sections are static and never reordered
          <Reveal key={i} w="100%" delay={Math.min(i, 4) * 60}>
            {child}
          </Reveal>
        ) : (
          child
        ),
      )}
    </VStack>
  )
}
